import { useEffect, useState } from "react";
import type { DoorSignPreview } from "@shared/types/foyer";
import DoorSignSurface from "./DoorSign";

/**
 * Same cadence as the panel's own poll (`useDoorSign`): an operator who changes
 * a status in the CMS sees the preview follow within one tick, without this tab
 * needing a realtime connection it has no screen token to open.
 */
const POLL_MS = 15_000;

type State =
  | { kind: "loading" }
  | { kind: "ready"; data: DoorSignPreview }
  | { kind: "error"; status: number | null; code?: string };

/**
 * Operator preview of a REAL door-sign screen - `?doorsignScreen=<id>&premise=<id>`.
 *
 * The panel renders its sign from a screen-token-gated projection; a preview
 * tab has no token, so it asks the CMS-cookie route that answers with the very
 * same projection plus the slice of config the sign needs. Rendering then goes
 * through the ordinary `DoorSignSurface` in preview mode, so there is one
 * renderer for the wall and the laptop.
 *
 * A refusal is captioned rather than left black: "this screen is not a door
 * sign" is exactly the answer the operator clicked preview to get.
 */
export default function DoorSignPreviewSurface({
  premiseId,
  screenId,
  apiBase = "",
}: {
  premiseId: string;
  screenId: string;
  apiBase?: string;
}) {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let alive = true;
    const url = `${apiBase}/api/frontlayer/v1/premise/${encodeURIComponent(
      premiseId
    )}/care/screens/${encodeURIComponent(screenId)}/door-sign`;
    const pull = async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!alive) return;
        if (!res.ok) {
          let code: string | undefined;
          try {
            code = ((await res.json()) as { error?: string }).error;
          } catch {
            // Not JSON (e.g. a proxy page) - the status alone will do.
          }
          if (alive) setState({ kind: "error", status: res.status, code });
          return;
        }
        const data = (await res.json()) as DoorSignPreview;
        if (alive) setState({ kind: "ready", data });
      } catch {
        // Network blip: keep the last good sign on screen, as the panel does.
        if (alive) {
          setState((prev) =>
            prev.kind === "ready" ? prev : { kind: "error", status: null }
          );
        }
      }
    };
    void pull();
    const t = window.setInterval(() => void pull(), POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [apiBase, premiseId, screenId]);

  if (state.kind === "ready") {
    const { data } = state;
    return (
      <DoorSignSurface
        config={data.config}
        roomId={data.roomId}
        orientation={data.orientation}
        preview
        previewProjection={data.projection}
      />
    );
  }

  return (
    <div
      data-testid="door-sign-preview-message"
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
        background: "#111",
        color: "#eee",
        fontFamily: "system-ui, sans-serif",
        fontSize: 20,
        textAlign: "center",
      }}
    >
      {state.kind === "loading" ? "Loading door sign…" : errorText(state)}
    </div>
  );
}

function errorText(state: Extract<State, { kind: "error" }>): string {
  switch (state.code) {
    case "not_a_door_sign":
      return "This screen is not a door sign. Switch its role to Door sign to preview it here.";
    case "no_room":
      return "This door sign has no room assigned, so there is nothing to show.";
    case "screen_not_found":
      return "This screen no longer exists on the premise.";
    case "premise_not_found":
      return "This premise could not be found.";
  }
  if (state.status === 401)
    return "Sign in to the CMS to preview this door sign.";
  if (state.status === 403)
    return "Your account cannot view this premise's door signs.";
  if (state.status === null) return "Could not reach the server. Retrying…";
  return `The door sign could not be loaded (HTTP ${state.status}).`;
}
